import { useMutation, useQuery } from "@tanstack/react-query";

import { type GroupId, type IBrandUnderGroupPayload } from "@/api/admin/groups";
import { QUERY_KEYS } from "@/api/constants";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";
import { type DataListForDetailsType } from "@/components/atoms";
import { type GroupInformationFormType } from "@/components/units/GroupInformationForm";
import { type PaginationParams } from "@/types/api";

import { groupsApi } from "./groupsApi";

export const useDeleteGroupMutation = () => {
  return useMutation({
    mutationFn: (id: GroupId) => {
      return groupsApi.deleteGroup(id);
    },
  });
};

export const useUpdateGroupMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: GroupId;
      data: Partial<GroupInformationFormType>;
    }) => {
      return groupsApi.patchGroup(id, data);
    },
  });
};

export const useGetGroupsQuery = (
  params: PaginationParams,
  enabled: boolean = true
) => {
  const query = useQuery({
    queryKey: [QUERY_KEYS.groups, params],
    queryFn: () => groupsApi.getGroups(params),
    enabled,
  });

  const filterCountries =
    query.data?.items?.reduce<string[]>((acc, item) => {
      if (item.country && !acc.includes(item.country)) {
        acc.push(item.country);
      }
      return acc;
    }, []) || [];

  return {
    ...query,
    filterCountries,
    data: query.data,
  };
};

export const useGetGroupByIdQuery = (id: GroupId) => {
  const query = useQuery({
    queryKey: [QUERY_KEYS.group, id],
    queryFn: () => groupsApi.getGroupById(id),
    enabled: !!id,
    meta: RESOURCE_QUERY_META,
  });

  const mapKeyToName = {
    referenceNumber: "Reference number",
    name: "Group name",
    country: "Country",
    // Reads currencyName, not currency: the stored value is the ISO code and
    // this list is for display.
    currencyName: "Currency",
    legalEntityName: "Legal entity",
    id: "Entity ID",
  } as const satisfies Partial<Record<keyof IBrandUnderGroupPayload, string>>;

  const detailsList: DataListForDetailsType[] = (
    Object.keys(mapKeyToName) as (keyof typeof mapKeyToName)[]
  ).map(key => {
    const value = query.data?.[key];
    return {
      title: mapKeyToName[key],
      value: value == null ? undefined : String(value),
      allowCopy: key === "id",
    };
  });

  return {
    ...query,
    data: query.data,
    detailsList,
    mapKeyToName,
  };
};
